import { useState } from 'react';
import {
  Box,
  Button,
  Center,
  Field,
  Flex,
  HStack,
  IconButton,
  Input,
  NativeSelect,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { HiPlus, HiTrash } from 'react-icons/hi2';
import {
  useLicenseTemplates,
  useCreateLicenseTemplate,
  useDeleteLicenseTemplate,
  useProducts,
  useModules,
} from '../../hooks/queries/useLicensing';
import type {
  LicenseChannel,
  LicenseTemplate,
  LicenseTemplateWritePayload,
} from '../../services/licensing.service';
import { useHasPermissions } from '../../utils/hasPermission';

const MANAGE_LICENSE = ['licensing.manage_license'];

const CHANNEL_OPTIONS: LicenseChannel[] = ['direct', 'reseller', 'oem'];

interface NewTemplateForm {
  name: string;
  product: number | '';
  default_channel: LicenseChannel;
  default_trial_days: number;
  default_grace_period_days: number;
  notes: string;
  module_codes: string[];
}

const EMPTY_FORM: NewTemplateForm = {
  name: '',
  product: '',
  default_channel: 'reseller',
  default_trial_days: 30,
  default_grace_period_days: 14,
  notes: '',
  module_codes: [],
};

function TemplateCard({
  template,
  onDelete,
}: {
  template: LicenseTemplate;
  onDelete: () => void;
}) {
  return (
    <Box
      bg="surface.primary"
      border="1px solid"
      borderColor="border.primary"
      borderRadius="lg"
      p={5}
    >
      <Flex justify="space-between" align="start" mb={3}>
        <Box>
          <Text fontWeight={600} fontSize="md" color="text.primary">
            {template.name}
          </Text>
          <Text fontSize="sm" color="text.secondary">
            {template.vendor_name ?? 'Aegix-global'} · {template.product_code.toUpperCase()}
          </Text>
        </Box>
        <IconButton
          aria-label="Delete template"
          size="sm"
          variant="ghost"
          onClick={onDelete}
        >
          <HiTrash />
        </IconButton>
      </Flex>
      <SimpleGrid columns={2} gap={3} mb={3}>
        <Box>
          <Text fontSize="xs" color="text.secondary">Channel</Text>
          <Text fontSize="sm" color="text.primary">{template.default_channel}</Text>
        </Box>
        <Box>
          <Text fontSize="xs" color="text.secondary">Trial days</Text>
          <Text fontSize="sm" color="text.primary">{template.default_trial_days}</Text>
        </Box>
        <Box>
          <Text fontSize="xs" color="text.secondary">Grace days</Text>
          <Text fontSize="sm" color="text.primary">{template.default_grace_period_days}</Text>
        </Box>
        <Box>
          <Text fontSize="xs" color="text.secondary">Type</Text>
          <Text fontSize="sm" color="text.primary">{template.default_type}</Text>
        </Box>
      </SimpleGrid>
      {template.template_modules.length > 0 && (
        <Box pt={3} borderTop="1px solid" borderColor="border.primary">
          <Text fontSize="xs" fontWeight={600} color="text.secondary" mb={2}>
            Modules
          </Text>
          <Flex gap={2} flexWrap="wrap">
            {template.template_modules.map((m) => (
              <Box
                key={m.module_code}
                px={2}
                py={1}
                bg="surface.secondary"
                borderRadius="md"
                fontSize="xs"
                color="text.primary"
              >
                {m.module_name}
              </Box>
            ))}
          </Flex>
        </Box>
      )}
      {template.notes && (
        <Text mt={3} fontSize="xs" color="text.tertiary">
          {template.notes}
        </Text>
      )}
    </Box>
  );
}

function NewTemplateForm({
  onCancel,
  onSaved,
}: {
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<NewTemplateForm>(EMPTY_FORM);
  const { data: products } = useProducts();
  const { data: modules } = useModules();
  const create = useCreateLicenseTemplate();

  const productOptions = products ?? [];
  const moduleOptions = (modules ?? []).filter(
    (m) => form.product !== '' && m.product === form.product,
  );

  const submit = async () => {
    if (!form.name || !form.product) return;
    const payload: LicenseTemplateWritePayload = {
      name: form.name,
      product: Number(form.product),
      default_channel: form.default_channel,
      default_trial_days: form.default_trial_days,
      default_grace_period_days: form.default_grace_period_days,
      notes: form.notes,
      module_codes: form.module_codes,
    };
    await create.mutateAsync(payload);
    onSaved();
  };

  const toggleModule = (code: string) => {
    setForm((prev) => ({
      ...prev,
      module_codes: prev.module_codes.includes(code)
        ? prev.module_codes.filter((c) => c !== code)
        : [...prev.module_codes, code],
    }));
  };

  return (
    <Box
      bg="surface.primary"
      border="1px solid"
      borderColor="border.primary"
      borderRadius="lg"
      p={5}
      mb={5}
    >
      <Text fontWeight={600} mb={4}>New license template</Text>
      <VStack align="stretch" gap={3}>
        <Field.Root gap={0}>
          <Field.Label fontSize="sm" color="text.secondary" mb={1}>Name</Field.Label>
          <Input
            value={form.name}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, name: e.target.value }))
            }
            placeholder="e.g. Starter"
          />
        </Field.Root>
        <Box>
          <Text fontSize="sm" color="text.secondary" mb={1}>Product</Text>
          <NativeSelect.Root>
            <NativeSelect.Field
              aria-label="Product"
              value={form.product === '' ? '' : String(form.product)}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  product: e.target.value === '' ? '' : Number(e.target.value),
                  module_codes: [],
                }))
              }
            >
              <option value="">Select a product</option>
              {productOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </NativeSelect.Field>
          </NativeSelect.Root>
        </Box>
        <SimpleGrid columns={3} gap={3}>
          <Box>
            <Text fontSize="sm" color="text.secondary" mb={1}>Channel</Text>
            <NativeSelect.Root>
              <NativeSelect.Field
                aria-label="Channel"
                value={form.default_channel}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    default_channel: e.target.value as LicenseChannel,
                  }))
                }
              >
                {CHANNEL_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </NativeSelect.Field>
            </NativeSelect.Root>
          </Box>
          <Field.Root gap={0}>
            <Field.Label fontSize="sm" color="text.secondary" mb={1}>Trial days</Field.Label>
            <Input
              type="number"
              value={form.default_trial_days}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  default_trial_days: Number(e.target.value),
                }))
              }
            />
          </Field.Root>
          <Field.Root gap={0}>
            <Field.Label fontSize="sm" color="text.secondary" mb={1}>Grace days</Field.Label>
            <Input
              type="number"
              value={form.default_grace_period_days}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  default_grace_period_days: Number(e.target.value),
                }))
              }
            />
          </Field.Root>
        </SimpleGrid>
        {moduleOptions.length > 0 && (
          <Box>
            <Text fontSize="sm" color="text.secondary" mb={1}>Modules enabled by default</Text>
            <Flex gap={2} flexWrap="wrap">
              {moduleOptions.map((m) => {
                const selected = form.module_codes.includes(m.code);
                return (
                  <Button
                    key={m.code}
                    size="xs"
                    variant={selected ? 'solid' : 'outline'}
                    onClick={() => toggleModule(m.code)}
                  >
                    {m.name}
                  </Button>
                );
              })}
            </Flex>
          </Box>
        )}
        <Field.Root gap={0}>
          <Field.Label fontSize="sm" color="text.secondary" mb={1}>Notes</Field.Label>
          <Textarea
            value={form.notes}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, notes: e.target.value }))
            }
            rows={2}
          />
        </Field.Root>
        <HStack justify="end" gap={2}>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!form.name || form.product === '' || create.isPending}
            loading={create.isPending}
          >
            Create template
          </Button>
        </HStack>
        {create.isError && (
          <Text color="text.danger" fontSize="sm">
            {(create.error as Error)?.message ?? 'Failed to create template'}
          </Text>
        )}
      </VStack>
    </Box>
  );
}

export function TemplatesContent() {
  const canManage = useHasPermissions(MANAGE_LICENSE);
  const { data: templates, isLoading, error } = useLicenseTemplates(canManage);
  const del = useDeleteLicenseTemplate();
  const [creating, setCreating] = useState(false);

  if (!canManage) {
    return (
      <Center py={12}>
        <Text color="text.secondary">
          You don't have permission to manage license templates.
        </Text>
      </Center>
    );
  }

  if (isLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={12}>
        <Text color="text.danger">
          {error instanceof Error ? error.message : 'Failed to load templates'}
        </Text>
      </Center>
    );
  }

  const handleDelete = async (template: LicenseTemplate) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;
    await del.mutateAsync(template.id);
  };

  return (
    <Box px={{ base: 4, md: 8 }} py={6}>
      <Flex justify="space-between" align="start" mb={4}>
        <Box>
          <Text fontSize="lg" fontWeight={600} color="text.primary">
            License Templates
          </Text>
          <Text fontSize="sm" color="text.secondary">
            Predefine the licensing shape applied when onboarding a new
            organization. See ADR 104 §18.
          </Text>
        </Box>
        {!creating && (
          <Button onClick={() => setCreating(true)} size="sm">
            <HiPlus /> New template
          </Button>
        )}
      </Flex>

      {creating && (
        <NewTemplateForm
          onCancel={() => setCreating(false)}
          onSaved={() => setCreating(false)}
        />
      )}

      {(templates ?? []).length === 0 ? (
        <Center py={8}>
          <Text color="text.secondary">
            No templates yet. Create one to enable vendor-driven onboarding.
          </Text>
        </Center>
      ) : (
        <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
          {(templates ?? []).map((t) => (
            <TemplateCard key={t.id} template={t} onDelete={() => handleDelete(t)} />
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
}
